import { type ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router";
import { parseWikiTarget } from "@justone/editor/syntax";
import { markdownToPlain, type UserDTO } from "@justone/shared";
import { errorMessage } from "../api/http";
import { useChannels, useMe, useStartConversation, useUserMap } from "../api/queries";
import { useWikiPage, useWikiSpaces, useWikiTitles } from "../api/wiki";
import { resolveWikiTarget, wikiPagePath } from "../lib/wiki";
import { Popover, useHoverIntent } from "./Popover";
import { Avatar, useToast } from "./ui";

/** 사람 카드: 이름·아이디·접속 상태, DM 보내기 */
export function UserCard({ user, onDone }: { user: UserDTO; onDone: () => void }) {
  const me = useMe();
  const start = useStartConversation();
  const navigate = useNavigate();
  const toast = useToast();
  return (
    <div className="user-card">
      <Avatar user={user} size={52} presence />
      <div className="uc-text">
        <strong>{user.displayName}</strong>
        {user.username && <span className="muted">@{user.username}</span>}
        <span className="muted">
          {!user.active ? "비활성" : user.online ? "지금 접속 중" : "접속하지 않음"}
          {user.role === "admin" ? " · 관리자" : ""}
        </span>
      </div>
      {user.id !== me.id && user.active && (
        <button
          type="button"
          className="btn btn-sm"
          disabled={start.isPending}
          onClick={() =>
            start.mutate([user.id], {
              onSuccess: (c) => {
                onDone();
                navigate(`/d/${c.id}`);
              },
              onError: (e) => toast(errorMessage(e)),
            })
          }
        >
          DM 보내기
        </button>
      )}
    </div>
  );
}

/** 누르면 사람 카드가 뜨는 단추 (보낸 사람 이름·아바타, @멘션) */
export function UserButton({ user, className, children, label }: { user: UserDTO | undefined; className?: string; children: ReactNode; label?: string }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!user) return <span className={className}>{children}</span>;
  return (
    <>
      <button type="button" className={`user-button ${className ?? ""}`} onClick={(e) => setAnchor(anchor ? null : e.currentTarget)} aria-haspopup="dialog" aria-expanded={!!anchor} aria-label={label}>
        {children}
      </button>
      <Popover anchor={anchor} open={!!anchor} onClose={() => setAnchor(null)} className="preview-card" role="dialog" label={`${user.displayName} 정보`}>
        <UserCard user={user} onDone={() => setAnchor(null)} />
      </Popover>
    </>
  );
}

/** 메시지 안의 @멘션: 아이디로 사람을 찾아 이름으로 보여 주고, 누르면 사람 카드 */
export function MentionChip({ name, isMe }: { name: string; isMe: boolean }) {
  const users = useUserMap();
  const lower = name.toLowerCase();
  const user = [...users.values()].find((u) => u.username === lower || u.displayName === name);
  return (
    <UserButton user={user} className={`chip chip-mention${isMe ? " me" : ""}`} label={user ? `@${user.displayName} (${user.username ?? name}) 정보` : undefined}>
      @{user?.displayName ?? name}
    </UserButton>
  );
}

/**
 * 메시지 안의 [[위키 링크]]: 같은 공간은 그 채널의 위키(DM은 공통 위키).
 * 있으면 누르면 가고 마우스를 올리면 첫 몇 줄, 없으면 흐리게.
 */
export function WikiLinkChip({ inner, channelId }: { inner: string; channelId: number | null }) {
  const { target, label } = parseWikiTarget(inner);
  const { data: spaces = [] } = useWikiSpaces();
  const { data: channels = [] } = useChannels();
  const { data: titles = [] } = useWikiTitles();
  const hover = useHoverIntent();
  const from = spaces.find((s) => s.channelId === channelId) ?? { id: -1, channelId, name: "" };
  const r = resolveWikiTarget(target, from, spaces, channels, titles);
  if (!r?.pageId)
    return (
      <span className="chip chip-wiki missing" title="아직 없는 위키 페이지입니다">
        {label}
      </span>
    );
  return (
    <>
      <Link className="chip chip-wiki" to={wikiPagePath(r.channelId, r.pageId)} onPointerEnter={(e) => e.pointerType === "mouse" && hover.enter(e.currentTarget)} onPointerLeave={hover.leave} onClick={hover.close}>
        {label}
      </Link>
      <Popover anchor={hover.anchor} open={!!hover.anchor} onClose={hover.close} className="preview-card" role="tooltip" onPointerEnter={hover.keep} onPointerLeave={hover.leave} passive>
        <WikiPreview pageId={r.pageId} />
      </Popover>
    </>
  );
}

function WikiPreview({ pageId }: { pageId: number }) {
  const { data: page, isError } = useWikiPage(pageId);
  const { data: spaces = [] } = useWikiSpaces();
  if (isError) return <p className="muted">페이지를 열 수 없습니다.</p>;
  if (!page) return <div className="skeleton-lines" aria-label="불러오는 중" />;
  const space = spaces.find((s) => s.id === page.spaceId);
  const text = markdownToPlain(page.markdown);
  return (
    <div className="wiki-preview">
      <span className="lp-site">위키 · {space?.name ?? ""}</span>
      <strong>{page.title}</strong>
      <p>{text ? (text.length > 220 ? `${text.slice(0, 220)}…` : text) : "아직 내용이 없습니다."}</p>
    </div>
  );
}
